'use client';

import React, { useState } from 'react';
import { PriceCategory, PriceListItem } from './types';

interface PriceListAccordionItemProps {
  data: PriceCategory;
  index: number;
}

interface PriceListSubItemProps {
  item: PriceListItem;
}

const CheckIcon = () => (
  <svg
    width="25"
    height="25"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mt-1 h-4 w-4 shrink-0"
  >
    <path
      d="M7.82812 0.318848C3.51483 0.318848 0 3.83368 0 8.14697C0 12.4603 3.51483 15.9751 7.82812 15.9751C12.1414 15.9751 15.6562 12.4603 15.6562 8.14697C15.6562 3.83368 12.1414 0.318848 7.82812 0.318848ZM11.57 6.3465L7.13142 10.7851C7.02183 10.8946 6.87309 10.9573 6.71653 10.9573C6.55997 10.9573 6.41123 10.8946 6.30164 10.7851L4.08628 8.56969C3.85927 8.34268 3.85927 7.96693 4.08628 7.73991C4.3133 7.51289 4.68905 7.51289 4.91606 7.73991L6.71653 9.54038L10.7402 5.51672C10.9672 5.28971 11.343 5.28971 11.57 5.51672C11.797 5.74374 11.797 6.11166 11.57 6.3465Z"
      fill="url(#paint0_linear_6226_7168)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_6226_7168"
        x1="1.32439"
        y1="6.66133"
        x2="15.4986"
        y2="7.87467"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--color-theme-information)" />
        <stop offset="1" stopColor="var(--color-theme-success-secondary)" />
      </linearGradient>
    </defs>
  </svg>
);

const PriceListSubItem: React.FC<PriceListSubItemProps> = ({ item }) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const hasDetails = Boolean(item.details?.length);
  const detailsId = `price-details-${item.name
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div>
      <div className="flex flex-col gap-2 md:flex-row md:justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4">
            <div className="text-theme-secondary-dark font-bold">
              {item.name}
            </div>

            {hasDetails && (
              <div
                onClick={() => setIsDetailsOpen((prev) => !prev)}
                className="bg-surface-icon-bg flex h-8 w-8 cursor-pointer items-center justify-center rounded-full"
              >
                <span className="text-button-primary-bg mb-1 text-[22px]">
                  {isDetailsOpen ? '-' : '+'}
                </span>
              </div>
            )}
          </div>

          {item.duration && (
            <div className="text-text-default mt-2 font-semibold">
              {item.duration}
            </div>
          )}
        </div>

        <div className="flex min-w-30 flex-col md:items-end">
          <div className="text-surface-button font-bold md:text-end">
            {item.price}
          </div>
          {item.priceNote && (
            <div className="text-text-default mt-1 text-base font-semibold">
              {item.priceNote}
            </div>
          )}
        </div>
      </div>

      {hasDetails && (
        <div
          id={detailsId}
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isDetailsOpen
              ? 'mt-6 max-h-[600px] opacity-100'
              : 'max-h-0 opacity-0'
          }`}
        >
          <ul className="flex flex-col gap-5">
            {item.details?.map((detail, detailIndex) => (
              <li
                key={`${detail}-${detailIndex}`}
                className="text-text-main flex items-start gap-5 text-[18px] leading-6 lg:text-[20px] lg:leading-8"
              >
                <div className="mt-1">{CheckIcon()}</div>
                <span>{detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const PriceListAccordionItem: React.FC<PriceListAccordionItemProps> = ({
  data,
  index,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const items = data?.items ?? [];
  const subCategories = data?.subCategories ?? [];
  const hasSubCategories = subCategories.length > 0;
  const lastIndex = items.length - 1;
  const lastSubCategoryIndex = subCategories.length - 1;
  const panelId = `accordion-panel-${index}`;

  return (
    <div className="w-full px-4">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between"
        aria-expanded={isOpen}
        aria-controls={panelId}
        type="button"
      >
        <h3 className="font-mulish text-surface-button text-left text-[20px] leading-6 font-bold tracking-[-0.52px] lg:text-[26px] lg:leading-8">
          {data?.categoryName}
        </h3>

        <div className="bg-surface-icon-bg flex h-8 w-8 items-center justify-center rounded-full">
          <span className="text-button-primary-bg mb-1 text-[22px]">
            {isOpen ? '-' : '+'}
          </span>
        </div>
      </button>

      <div
        id={panelId}
        className={`mt-4 w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="font-regular text-text-main relative min-h-0 text-[16px] leading-5.5 tracking-[-0.48px] md:text-[18px] lg:leading-6.5">
          {hasSubCategories ? (
            <div className="flex flex-col gap-4">
              {subCategories.map((subCategory, subIndex) => (
                <React.Fragment key={subCategory.name ?? subIndex}>
                  <div className="flex flex-col gap-2">
                    <h4 className="text-theme-primary text-base font-bold md:text-lg">
                      {subCategory.name}
                    </h4>

                    <div className="flex flex-col gap-2">
                      {(subCategory.items ?? []).map((item, i) => (
                        <PriceListSubItem key={item.name ?? i} item={item} />
                      ))}
                    </div>
                  </div>

                  {subIndex !== lastSubCategoryIndex && (
                    <span className="border-border-info-muted h-1 w-full border-t-2 border-dashed" />
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((item, i) => (
                <React.Fragment key={item.name ?? i}>
                  <PriceListSubItem item={item} />

                  {i !== lastIndex && (
                    <span className="border-border-info-muted h-1 w-full border-t-2 border-dashed" />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceListAccordionItem;
